import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getSubjectById } from '../../config/subjects';
import { useAuth } from '../Auth/AuthContext';

function SubjectDetail() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const subject = getSubjectById(subjectId);

  // If subject not found, show error
  if (!subject) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-quicksand">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-study-black mb-4">Subject Not Found</h1>
          <p className="text-study-black mb-6">The subject you're looking for doesn't exist.</p>
          <Link
            to="/academy"
            className="bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-yellow transition-all"
          >
            Back to Essentials
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-quicksand">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <button
          onClick={() => navigate('/academy')}
          className="flex items-center text-study-black hover:text-study-teal transition-all"
        >
          <span className="text-2xl mr-2">←</span>
          <span className="font-bold">Back to Essentials</span>
        </button>
      </div>

      {/* Subject Hero */}
      <div className={`bg-gradient-to-br ${subject.color} py-16 px-6 text-center mt-4`}>
        <div className="text-8xl mb-4">{subject.icon}</div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          {subject.name}
        </h1>
        <p className="text-white text-xl max-w-2xl mx-auto mb-6">
          {subject.description}
        </p>
        <div className="flex justify-center gap-8 text-white">
          <div>
            <div className="text-3xl font-bold">{subject.questionCount}</div>
            <div className="text-sm">Practice Questions</div>
          </div>
          <div>
            <div className="text-3xl font-bold">
              {[subject.hasStudyGuides, subject.hasFlashcards, subject.hasVideos].filter(Boolean).length}
            </div>
            <div className="text-sm">Resource Types</div>
          </div>
        </div>
      </div>

      {/* Resources Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-study-black mb-8">Available Resources</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Study Guides Card */}
          <ResourceCard
            title="Study Guides"
            icon="📚"
            description="Comprehensive PDF guides covering key concepts"
            available={subject.hasStudyGuides}
            route={`/academy/${subject.id}/study-guides`}
            isSubscribed={isSubscribed}
            isPremium={true}
          />

          {/* Flashcards Card */}
          <ResourceCard
            title="Flashcards"
            icon="🗂️"
            description="Interactive flashcards for quick review"
            available={subject.hasFlashcards}
            route={`/academy/${subject.id}/flashcards`}
            isSubscribed={isSubscribed}
            isPremium={true}
          />

          {/* Videos Card */}
          <ResourceCard
            title="Videos"
            icon="🎥"
            description="Video tutorials and explanations"
            available={subject.hasVideos}
            route={`/academy/${subject.id}/videos`}
            isSubscribed={isSubscribed}
            isPremium={true}
          />
        </div>

        {/* Quiz Practice CTA */}
        <div className="mt-12 bg-gradient-to-r from-study-teal to-study-pink rounded-2xl p-8 text-center">
          <h3 className="text-3xl font-bold text-study-black mb-4">
            Ready to Test Your Knowledge?
          </h3>
          <p className="text-study-black text-lg mb-6">
            Practice with {subject.questionCount} questions specifically for {subject.name}
          </p>
          <Link
            to="/quiz-bank"
            state={{ subject: subject.name, count: 25 }}
            className="inline-block bg-study-yellow text-study-black font-bold text-lg py-4 px-8 rounded-full hover:bg-white transition-all transform hover:scale-105 shadow-lg"
          >
            Start Quiz Practice
          </Link>
        </div>
      </div>
    </div>
  );
}

function ResourceCard({ title, icon, description, available, route, isSubscribed, isPremium }) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!available) {
      return; // Coming soon - do nothing
    }
    if (isPremium && !isSubscribed) {
      navigate('/pricing');
      return;
    }
    navigate(route);
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-xl shadow-md p-6 transition-all ${
        available
          ? 'hover:shadow-xl hover:scale-105 cursor-pointer'
          : 'opacity-60 cursor-not-allowed'
      }`}
    >
      <div className="text-5xl mb-4 text-center">{icon}</div>
      <h3 className="text-2xl font-bold text-study-black mb-2 text-center">{title}</h3>
      <p className="text-study-black text-sm mb-4 text-center">{description}</p>

      {/* Status Badge */}
      {!available ? (
        <div className="bg-gray-200 text-gray-600 text-center py-2 px-4 rounded-lg font-semibold">
          Coming Soon
        </div>
      ) : isPremium && !isSubscribed ? (
        <div className="bg-study-yellow text-study-black text-center py-2 px-4 rounded-lg font-semibold">
          Premium Only 🔒
        </div>
      ) : (
        <div className="bg-study-teal text-study-black text-center py-2 px-4 rounded-lg font-semibold hover:bg-study-pink transition-all">
          View Resources →
        </div>
      )}
    </div>
  );
}

export default SubjectDetail;
